import { useNavigate } from 'react-router-dom'
import './Partner.scss'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import GoalsModal from './GoalsModal/GoalsModal'
import {
  useStaffGetCurrentPartnershipInformation,
  useStaffGetEntityNumbers,
} from 'services'
import dayjs from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import {
  AgxButton,
  AgxDivider,
  AgxDropdown,
  AgxEntityStatistics,
  AgxSpacer,
  AgxTab,
  AgxTypography,
  Breakpoints,
  DropdownOption,
  SaleGoals,
  TabOption,
  useToast,
  useWindowSize,
} from '@urbanx/agx.ui-components2'
import DateRangeSelector from 'pages/network/DateRangeSelector'
import { Pages } from 'constants/menu'
import PartnershipTab from './Tabs/PartnershipTab'
import useAgencyIdParam from 'hooks/useAgencyIdParam'
import useAgentIdParam from 'hooks/useAgentIdParam'
import { exportStatsToCSV } from './ExportToCSV/exportStatsToCSV'
import {
  ContractDateToUse,
  StatisticsForEntityRequestTimePeriod,
  PartnershipDetailsPartnershipLevel,
  StatisticsForEntityRequestEntityType,
} from '@urbanx/agx.rex.client'
import { useSelectedAgency } from 'hooks'
import {
  AgxMultiOptionButton,
  Images,
  useDebouncedState,
} from '@urbanx/agx-ui-components'
import {
  useInvalidateUseStaffGetAnnualBusinessTargets,
  useStaffGetAnnualBusinessTargets,
  useStaffSetAnnualBusinessTargets,
} from '@urbanx/agx.agencies.client'
import { SelectedPartnerTab } from './SelectedPartnerTab'

dayjs.extend(quarterOfYear)

const TimePeriods: DropdownOption[] = [
  {
    value: StatisticsForEntityRequestTimePeriod.Month,
    label: 'Month',
  },
  {
    value: StatisticsForEntityRequestTimePeriod.Quarter,
    label: 'Quarter',
  },
  {
    value: StatisticsForEntityRequestTimePeriod.Annual,
    label: 'Year',
  },
  {
    value: StatisticsForEntityRequestTimePeriod.Previous,
    label: 'Last 12 months',
  },
  {
    value: StatisticsForEntityRequestTimePeriod.Custom,
    label: 'Custom',
  },
]

const contractTypeOptions: DropdownOption[] = [
  {
    value: ContractDateToUse.Written,
    label: 'Written',
  },
  {
    value: ContractDateToUse.Unconditional,
    label: 'Unconditional',
  },
  {
    value: ContractDateToUse.Settled,
    label: 'Settled',
  },
]

enum PartnershipYears {
  ThisYear = 'ThisYear',
  LastYear = 'LastYear',
}

const partnershipYearOptions: DropdownOption[] = [
  { label: 'This Year', value: PartnershipYears.ThisYear },
  { label: 'Last Year', value: PartnershipYears.LastYear },
]

interface PartnerProps {
  individual?: boolean
}

const downloadCsv = (csvString: string, filename: string) => {
  const blob = new Blob([csvString], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.setAttribute('hidden', '')
  a.setAttribute('href', url)
  a.setAttribute('download', filename)
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

function getInitialDates() {
  const today = dayjs()

  return {
    startDate: today.startOf('month').add(1, 'day').format('YYYY-MM-DD'),
    endDate: today.format('YYYY-MM-DD'),
  }
}

const initialDates = getInitialDates()

const Partner = ({ individual = false }: PartnerProps) => {
  const navigate = useNavigate()

  const agencyId = useAgencyIdParam()
  const agentId = useAgentIdParam()
  const queryClient = useQueryClient()
  const { agency: selectedAgency } = useSelectedAgency()

  const entityId = useMemo(() => {
    const id = individual ? agentId : agencyId
    if (!id) {
      throw new Error('Entity ID is required')
    }
    return id
  }, [individual, agentId, agencyId])

  const [{ startDate, endDate }, setSelectedDate] = useDebouncedState<{
    startDate: string
    endDate: string
  }>(initialDates, 500)

  const [timePeriod, setTimePeriod] =
    useState<StatisticsForEntityRequestTimePeriod>(
      TimePeriods[0].value as StatisticsForEntityRequestTimePeriod
    )
  const [contractType, setContractType] = useState<ContractDateToUse>(
    contractTypeOptions[1].value as ContractDateToUse
  )
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const isCustomDateSelected = useMemo(() => {
    return timePeriod == StatisticsForEntityRequestTimePeriod.Custom
  }, [timePeriod])

  useEffect(() => {
    const today = dayjs()
    let calculatedStartDate = today
    const calculatedEndDate = today.format('YYYY-MM-DD')

    switch (timePeriod) {
      case StatisticsForEntityRequestTimePeriod.Month:
        calculatedStartDate = today.startOf('month').add(1, 'day')
        break
      case StatisticsForEntityRequestTimePeriod.Quarter:
        calculatedStartDate = today.startOf('quarter').add(1, 'day')
        break
      case StatisticsForEntityRequestTimePeriod.Annual:
        calculatedStartDate = today.startOf('year').add(1, 'day')
        break
      case StatisticsForEntityRequestTimePeriod.Previous:
        calculatedStartDate = today.add(-1, 'year').add(1, 'day')
        break
      default:
        calculatedStartDate = today
    }

    setSelectedDate({
      startDate: calculatedStartDate.format('YYYY-MM-DD'),
      endDate: calculatedEndDate,
    })
  }, [timePeriod])

  const [selectedTab, setSelectedTab] = useState<SelectedPartnerTab>(
    !individual ? SelectedPartnerTab.Partnership : SelectedPartnerTab.Statistics
  )

  const [selectedPartnershipYear, setSelectedPartnershipYear] = useState(
    PartnershipYears.ThisYear
  )

  const isPreviousYear = useMemo(() => {
    return selectedPartnershipYear === PartnershipYears.LastYear
  }, [selectedPartnershipYear])

  // Memoize callback functions to prevent re-renders
  const handleContractTypeChange = useCallback((value: string) => {
    setContractType(value as ContractDateToUse)
  }, [])

  const handleTimePeriodChange = useCallback((value: string) => {
    setTimePeriod(value as StatisticsForEntityRequestTimePeriod)
  }, [])

  const handleDateRangeChange = useCallback(
    (dates: { startDate: string; endDate: string }) => {
      setSelectedDate(dates)
    },
    []
  )

  const handlePartnershipYearChange = useCallback((value: string) => {
    setSelectedPartnershipYear(value as PartnershipYears)
  }, [])

  const handlePartnershipSettingsClick = useCallback(
    () => navigate(`/${Pages.AGENCIES}/${agencyId}/${Pages.PARTNERSHIP}`),
    [navigate, agencyId]
  )

  const handlePrintClick = useCallback(() => window.print(), [])

  const entityType = useMemo(() => {
    if (individual) {
      return StatisticsForEntityRequestEntityType.IndividualOrTeam
    }
    if (selectedTab === SelectedPartnerTab.Partnership) {
      return StatisticsForEntityRequestEntityType.Partnership
    }

    return StatisticsForEntityRequestEntityType.Agency
  }, [individual, selectedTab])

  const contractDateToUse = useMemo(() => {
    if (selectedTab === SelectedPartnerTab.Partnership) {
      return ContractDateToUse.Settled
    }

    return contractType
  }, [selectedTab, contractType])

  const statisticsTimePeriod = useMemo(() => {
    if (selectedTab === SelectedPartnerTab.Partnership) {
      if (isPreviousYear)
        return StatisticsForEntityRequestTimePeriod.LastPartnershipYear
      return StatisticsForEntityRequestTimePeriod.ThisPartnershipYear
    }

    return timePeriod
  }, [timePeriod, isPreviousYear, selectedTab])

  const { data: partnershipNumbers, isPending: isPartnershipNumbersPending } =
    useStaffGetEntityNumbers(
      entityId,
      contractDateToUse,
      startDate,
      endDate,
      entityType,
      statisticsTimePeriod
    )

  const { data: agencyPartnershipStatistics } =
    useStaffGetCurrentPartnershipInformation(
      entityId,
      undefined,
      isPreviousYear
    )

  const [annualBusinessTargets, setAnnualBusinessTargets] = useState<SaleGoals>(
    {
      gciGoal: 0,
      averageSalePrice: 0,
      averageCommissionRate: 0,
      averageConversionRate: 0,
    }
  )

  const {
    data: annualBusinessTargetsData,
    isPending: isAnnualBusinessTargetsPending,
  } = useStaffGetAnnualBusinessTargets(agencyId ?? '', !!agencyId)

  //Update ABT with request data
  useEffect(() => {
    if (annualBusinessTargetsData) {
      setAnnualBusinessTargets({
        gciGoal: annualBusinessTargetsData.gciGoal ?? 0,
        averageSalePrice: annualBusinessTargetsData.averageSalePrice ?? 0,
        averageCommissionRate:
          annualBusinessTargetsData.averageCommissionRate ?? 0,
        averageConversionRate:
          annualBusinessTargetsData.averageConversionRate ?? 0,
      })
    }
  }, [annualBusinessTargetsData])

  const { pushToast } = useToast()

  const invalidateStaffGetAnnualBusinessTargets =
    useInvalidateUseStaffGetAnnualBusinessTargets()

  const { staffSetAnnualBusinessTargets } = useStaffSetAnnualBusinessTargets({
    onSuccess: () => {
      Promise.all([
        // any matching query gets refreshed
        queryClient.invalidateQueries({
          queryKey: ['StaffGetEntityNumbers'],
          exact: false,
        }),
        invalidateStaffGetAnnualBusinessTargets(agencyId ?? ''),
      ])
      pushToast({
        message: 'Annual Goals updated successfully',
        type: 'success',
      })
    },
    onError: () => {
      pushToast({
        message: 'Error updating annual goals',
        type: 'error',
      })
    },
  })

  const handleSaveGoals = useCallback((goals: SaleGoals) => {
    try {
      staffSetAnnualBusinessTargets({
        agencyId: agencyId!,
        saleGoals: goals,
      })
    } catch (error) {
      console.error('Error saving goals:', error)
      throw new Error(`Error saving goals: ${error}`)
    }
  }, [])

  const tabOptions: TabOption[] = useMemo(() => {
    return [
      !individual
        ? {
            id: SelectedPartnerTab.Partnership.toString(),
            label: SelectedPartnerTab.Partnership.toString(),
            selected: selectedTab === SelectedPartnerTab.Partnership,
          }
        : undefined,
      {
        id: SelectedPartnerTab.Statistics.toString(),
        label: SelectedPartnerTab.Statistics.toString(),
        selected: selectedTab === SelectedPartnerTab.Statistics,
      },
    ].filter((option) => option !== undefined)
  }, [selectedTab, individual])

  const windowSize = useWindowSize()

  return (
    <>
      <AgxSpacer
        direction="vertical"
        gap="lg"
        padding="xl"
        className="partnerPage"
      >
        <AgxSpacer direction="vertical" fill gap="lg">
          <AgxSpacer
            direction={
              windowSize === Breakpoints.Mobile ? 'vertical' : 'horizontal'
            }
            fill
            contentAlignment={
              windowSize === Breakpoints.Mobile ? 'start' : 'center'
            }
            contentJustification="space-between"
            gap={windowSize === Breakpoints.Mobile ? 'md' : undefined}
          >
            <AgxTab
              options={tabOptions}
              onChange={(selectedTab) =>
                setSelectedTab(selectedTab.id as SelectedPartnerTab)
              }
            />
            <AgxSpacer
              direction={
                windowSize === Breakpoints.Mobile ? 'vertical' : 'horizontal'
              }
              gap="xs"
              contentAlignment={
                windowSize === Breakpoints.Mobile ? 'start' : 'center'
              }
              style={{
                flexBasis: 'content',
              }}
              fill={windowSize === Breakpoints.Mobile}
            >
              {selectedTab == SelectedPartnerTab.Statistics && (
                <>
                  <AgxSpacer
                    direction="horizontal"
                    gap="xs"
                    contentAlignment="center"
                    fill={windowSize === Breakpoints.Mobile}
                  >
                    <AgxDropdown
                      id="contractType"
                      options={contractTypeOptions}
                      selected={contractType}
                      onChange={handleContractTypeChange}
                      noHeader
                      type="single"
                    />
                    <AgxDropdown
                      id="timePeriodSelector"
                      selected={timePeriod}
                      options={TimePeriods}
                      onChange={handleTimePeriodChange}
                      noHeader
                      type="single"
                    />
                  </AgxSpacer>

                  {isCustomDateSelected && (
                    <DateRangeSelector
                      startDate={startDate}
                      endDate={endDate}
                      onChange={handleDateRangeChange}
                    />
                  )}
                </>
              )}

              {selectedTab === SelectedPartnerTab.Partnership && (
                <>
                  <AgxDropdown
                    id={'partnershipYearSelector'}
                    options={partnershipYearOptions}
                    selected={selectedPartnershipYear}
                    onChange={handlePartnershipYearChange}
                    noHeader
                    type="single"
                  />
                  <AgxSpacer
                    direction={'horizontal'}
                    gap="sm"
                    contentAlignment="center"
                  >
                    <AgxButton
                      text="Edit Goals"
                      size="medium"
                      variant="hollow"
                      onClick={() => setIsDialogOpen((prev) => !prev)}
                      id={'editGoalsButton'}
                      stretch
                    />
                    <AgxButton
                      text="Partnership Settings"
                      size="medium"
                      variant="hollow"
                      onClick={handlePartnershipSettingsClick}
                      id={'partnershipSettingsbutton'}
                      stretch
                    />
                  </AgxSpacer>
                </>
              )}
              <AgxMultiOptionButton
                primary
                medium
                options={[
                  {
                    text: 'PDF file',
                    onClick: handlePrintClick,
                  },
                  {
                    text: 'CSV file',
                    onClick: () => {
                      const [filename, csvString] = exportStatsToCSV(
                        selectedAgency,
                        selectedTab,
                        startDate,
                        endDate,
                        partnershipNumbers.statistics!,
                        agencyPartnershipStatistics?.partnershipLevel ??
                          PartnershipDetailsPartnershipLevel.Bronze
                      )
                      downloadCsv(csvString, filename)
                    },
                  },
                ]}
                rightIcon={<Images.CloudDownload />}
                text="Export"
              />
            </AgxSpacer>
          </AgxSpacer>
          {isDialogOpen && !isAnnualBusinessTargetsPending && (
            <GoalsModal
              saveGoals={handleSaveGoals}
              goals={annualBusinessTargets}
              setIsDialogOpen={setIsDialogOpen}
            />
          )}
          <AgxDivider variant={'horizontal'} />
          <AgxSpacer direction="vertical" gap="spacing-2xl" fill>
            <AgxTypography
              id={'partner-statistics-title'}
              type={'heading'}
              size={'small'}
            >
              {partnershipNumbers?.title}
            </AgxTypography>
            {selectedTab === SelectedPartnerTab.Statistics &&
              partnershipNumbers?.statistics !== undefined && (
                <AgxEntityStatistics
                  loading={isPartnershipNumbersPending}
                  personalStatistics={partnershipNumbers.statistics!}
                />
              )}
            {selectedTab === SelectedPartnerTab.Partnership &&
              partnershipNumbers?.statistics !== undefined && (
                <PartnershipTab
                  entityId={entityId}
                  entityNumbers={partnershipNumbers.statistics!}
                  currentReviewPeriodDetails={agencyPartnershipStatistics}
                  isPreviousYear={isPreviousYear}
                  custumizedPartnershipAgreement={
                    selectedAgency?.agency?.partnershipDetails
                      ?.partnershipAgreement?.customized ?? false
                  }
                />
              )}
          </AgxSpacer>
        </AgxSpacer>
      </AgxSpacer>
    </>
  )
}

export default Partner
